// Registry-keyed access to control content, keyed by frameworkId (ADR-2) rather
// than a framework-name literal.
import { nistControlsByDomain, nistControlDetails } from './controls/nist-controls';
import { isoControlsByDomain, isoControlDetails } from './controls/iso-controls';
import { CIS_GROUPED, CIS_DETAILS } from './generated/cis.generated';
import { mappedControlsByDomain } from './generated/domain-ttps.generated';
import { NIST_CONTROL_STUBS } from './generated/nist-attack.generated';

// NIST capability-group code → readable family name (from the CTID mapping metadata).
const NIST_FAMILY = {
  AC: 'Access Control', AU: 'Audit and Accountability', CA: 'Assessment, Authorization, and Monitoring',
  CM: 'Configuration Management', CP: 'Contingency Planning', IA: 'Identification and Authentication',
  MP: 'Media Protection', PL: 'Planning', PM: 'Program Management', RA: 'Risk Assessment',
  SA: 'System and Services Acquisition', SC: 'System and Communications Protection',
  SI: 'System and Information Integrity', SR: 'Supply Chain Risk Management',
};
const STUB = Object.fromEntries(NIST_CONTROL_STUBS.map((s) => [s.id, s]));

// Merge the hand-authored NIST columns with the data-grounded "applicable controls"
// derived from the NIST↔ATT&CK mappings (controls that mitigate the pillar's
// techniques). Curated entries keep their richer details; mapped-only controls get
// a name + family from the mapping. Build once at module load.
function buildNist() {
  const byDomain = {};
  const details = { ...nistControlDetails };

  for (const [domain, curated] of Object.entries(nistControlsByDomain)) {
    const merged = { ...curated };
    // Controls already shown in this domain's curated categories.
    const existing = new Set();
    for (const cat of Object.values(curated)) {
      for (const id of cat?.controls || []) existing.add(id);
    }
    const extra = (mappedControlsByDomain[domain] || []).filter((id) => !existing.has(id));
    if (extra.length) merged['Applicable Controls (ATT&CK-mapped)'] = { controls: extra };

    // Ensure every mapped control has at least a name/family detail.
    for (const id of mappedControlsByDomain[domain] || []) {
      if (!details[id] && STUB[id]) {
        details[id] = { name: STUB[id].name, family: NIST_FAMILY[STUB[id].family] || STUB[id].family };
      }
    }
    byDomain[domain] = merged;
  }
  return { byDomain, details };
}

const NIST = buildNist();

export const controlSources = {
  'nist-800-53-r5': { byDomain: NIST.byDomain, details: NIST.details },
  'iso-27001-2022': { byDomain: isoControlsByDomain, details: isoControlDetails },
  // CIS is not Zero-Trust-pillar structured, so it is domain-agnostic.
  'cis-v8.1': { grouped: CIS_GROUPED, details: CIS_DETAILS, domainAgnostic: true },
};

export function getGroupedControls(frameworkId, domainId) {
  const src = controlSources[frameworkId];
  if (!src) return {};
  if (src.domainAgnostic) return src.grouped;
  return src.byDomain[domainId] || {};
}

export function getControlDetail(frameworkId, controlId) {
  const src = controlSources[frameworkId];
  return src ? src.details[controlId] || null : null;
}
